import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
    const { login } = useAuth();
    const navigate  = useNavigate();
    const [form, setForm]   = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            await login(form.email, form.password);
            navigate('/sales/invoices');
        } catch (err) {
            setError(err.response?.data?.error || 'Login failed. Please try again.');
        } finally { setLoading(false); }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing:border-box; margin:0; padding:0; }
                body { font-family:'Inter',sans-serif; }

                .auth-input {
                    width:100%; padding:13px 16px; border-radius:10px;
                    border:1.5px solid #e2e8f0; font-size:15px;
                    font-family:'Inter',sans-serif; color:#1e293b; background:#f8fafc;
                    outline:none; transition:all 0.2s ease;
                }
                .auth-input:focus { border-color:#3b82f6; background:#fff; box-shadow:0 0 0 4px rgba(59,130,246,0.1); }
                .auth-btn {
                    width:100%; padding:15px; border:none; border-radius:12px;
                    font-size:16px; font-weight:700; font-family:'Inter',sans-serif;
                    cursor:pointer; transition:all 0.25s cubic-bezier(0.4,0,0.2,1);
                    display:flex; align-items:center; justify-content:center; gap:8px;
                }
                .auth-btn:hover { transform:translateY(-2px); }
                .auth-btn:active { transform:scale(0.98); }

                @keyframes fadeSlideUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
                @keyframes floatBg { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-20px)} }
                .auth-card { animation:fadeSlideUp 0.5s cubic-bezier(0.4,0,0.2,1); }
                .blob1 { animation:floatBg 6s ease-in-out infinite; }
                .blob2 { animation:floatBg 8s ease-in-out infinite reverse; }
            `}</style>

            <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#0b1628 0%,#1e3a5f 50%,#0b1628 100%)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', position:'relative', overflow:'hidden' }}>

                {/* Decorative blobs */}
                <div className="blob1" style={{ position:'absolute', width:'400px', height:'400px', borderRadius:'50%', background:'radial-gradient(circle,rgba(59,130,246,0.15),transparent 70%)', top:'-100px', left:'-100px', pointerEvents:'none' }} />
                <div className="blob2" style={{ position:'absolute', width:'350px', height:'350px', borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,0.12),transparent 70%)', bottom:'-80px', right:'-80px', pointerEvents:'none' }} />

                <div className="auth-card" style={{ width:'100%', maxWidth:'440px', margin:'20px', background:'rgba(255,255,255,0.98)', borderRadius:'24px', padding:'44px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)' }}>

                    {/* Brand */}
                    <div style={{ textAlign:'center', marginBottom:'36px' }}>
                        <div style={{ width:'56px', height:'56px', background:'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius:'16px', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'28px', marginBottom:'16px', boxShadow:'0 8px 20px rgba(99,102,241,0.4)' }}>🛡️</div>
                        <h1 style={{ fontSize:'26px', fontWeight:'800', color:'#0f172a', letterSpacing:'-0.5px' }}>Welcome back</h1>
                        <p style={{ fontSize:'14px', color:'#94a3b8', marginTop:'6px' }}>Sign in to your invoice dashboard</p>
                    </div>

                    {error && (
                        <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', color:'#dc2626', padding:'12px 16px', borderRadius:'10px', fontSize:'14px', marginBottom:'20px', display:'flex', alignItems:'center', gap:'8px' }}>
                            ⚠️ {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom:'16px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'7px' }}>📧 Email Address</label>
                            <input className="auth-input" type="email" placeholder="you@company.com" required
                                value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                        </div>

                        <div style={{ marginBottom:'24px' }}>
                            <label style={{ display:'block', fontSize:'13px', fontWeight:'600', color:'#475569', marginBottom:'7px' }}>🔒 Password</label>
                            <div style={{ position:'relative' }}>
                                <input className="auth-input" type={showPass ? 'text' : 'password'} placeholder="Enter your password" required
                                    value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={{ paddingRight:'50px' }} />
                                <button type="button" onClick={() => setShowPass(!showPass)}
                                    style={{ position:'absolute', right:'14px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'18px', color:'#94a3b8' }}>
                                    {showPass ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>

                        <button className="auth-btn" type="submit" disabled={loading}
                            style={{ background: loading ? '#94a3b8' : 'linear-gradient(135deg,#3b82f6,#2563eb)', color:'white', boxShadow: loading ? 'none' : '0 6px 20px rgba(59,130,246,0.4)', marginBottom:'16px' }}>
                            {loading ? '⏳ Signing In...' : '🚀 Sign In to Dashboard'}
                        </button>
                    </form>

                    <div style={{ textAlign:'center', fontSize:'14px', color:'#64748b', borderTop:'1px solid #f1f5f9', paddingTop:'20px', marginTop:'8px' }}>
                        Don't have an account?{' '}
                        <Link to="/register" style={{ color:'#3b82f6', fontWeight:'700', textDecoration:'none' }}>Create Free Account →</Link>
                    </div>
                </div>
            </div>
        </>
    );
}
